import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert, Grid, Card, CardContent,
  Chip, IconButton, Tooltip, Avatar, Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  ToggleOn as EnableIcon,
  ToggleOff as DisableIcon,
  Add as AddIcon,
  CalendarMonth as AgendaIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getServiceName } from '../../utils/serviceTranslations';
import ScrollableDialog from '../../components/common/ScrollableDialog';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import { validateImageFile, ALLOWED_IMAGE_EXTENSIONS } from '../../utils/imageValidation';
import { resizeImageForUpload } from '../../utils/imageResize';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import TeamMemberBillingConfirmDialog from '../../components/team-member-registration/TeamMemberBillingConfirmDialog';
import StoreService from '../../services/storeService';
import { acceptsCustomerBookings as memberAcceptsCustomerBookings } from '../../utils/teamMemberBookingUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import ProCrownIcon from '../../components/billing/ProCrownIcon';
import { useProUpgrade } from '../../context/ProUpgradeContext';

const cardActionButtonSx = {
  width: { xs: 44, sm: 34 },
  height: { xs: 44, sm: 34 },
};

const TeamMembersPage = () => {
  const { t, i18n } = useTranslation();
  const { storeId } = useParams();
  const { hasPermission } = usePermissions(storeId);
  const { isPro, entitlements } = useStorePlan(storeId, Boolean(storeId));
  const allowsExtraMembers = isPro && entitlements?.allowsExtraTeamMembers !== false;
  const { openProUpgrade } = useProUpgrade();
  const canManageCompensation = hasPermission(Permissions.EmployeePaymentsSettingsUpdate);

  // Track renders
  const renderCount = React.useRef(0);
  renderCount.current += 1;

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedServices, setExpandedServices] = useState(new Set());
  const [pictureUploadingId, setPictureUploadingId] = useState(null);
  const [pictureDeletingId, setPictureDeletingId] = useState(null);
  const [confirmDeletePictureOpen, setConfirmDeletePictureOpen] = useState(false);
  const [pictureDeleteTarget, setPictureDeleteTarget] = useState(null);
  const [pictureModalOpen, setPictureModalOpen] = useState(false);
  const [pictureModalTeamMember, setPictureModalTeamMember] = useState(null);
  const [pictureError, setPictureError] = useState(null);
  const [enableBillingImpact, setEnableBillingImpact] = useState(null);
  const [showEnableBillingConfirm, setShowEnableBillingConfirm] = useState(false);
  const [pendingEnableTeamMemberId, setPendingEnableTeamMemberId] = useState(null);
  const [statusSnackbar, setStatusSnackbar] = useState({ open: false, message: '' });
  const pictureFileInputRef = useRef(null);
  const pendingPictureTeamMemberIdRef = useRef(null);

  // Toggle services expansion for a team member
  const toggleServicesExpansion = useCallback((teamMemberId) => {
    setExpandedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamMemberId)) {
        newSet.delete(teamMemberId);
      } else {
        newSet.add(teamMemberId);
      }
      return newSet;
    });
  }, []);

  // Load team members data
  const loadTeamMembers = async () => {
    if (!storeId) {
      setError(t('teamMembers.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    const requestKey = `getTeamMembers-${storeId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);
        const teamMembersData = await TeamMemberService.getTeamMembersByStore(storeId);
        setTeamMembers(teamMembersData);
      } catch (err) {
        console.error('Failed to load team members:', err);
        setError(t('teamMembers.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    loadTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // Filter team members based on search term
  const filteredTeamMembers = teamMembers;

  const handleToggleStatus = async (teamMemberId) => {
    if (!storeId) {
      console.error('Store ID is required to toggle team member status');
      return;
    }

    const teamMember = teamMembers.find(tm => tm.id === teamMemberId);
    const newDisabledStatus = !teamMember?.disabled;

    if (!newDisabledStatus) {
      try {
        const impact = await StoreService.getTeamMemberBillingImpact(storeId, { projectedAdditionalMember: true });
        if (impact?.willIncurAdditionalCharge) {
          setEnableBillingImpact(impact);
          setPendingEnableTeamMemberId(teamMemberId);
          setShowEnableBillingConfirm(true);
          return;
        }
      } catch (err) {
        console.error('Failed to load team member billing impact:', err);
      }
    }

    await applyTeamMemberStatusChange(teamMemberId, newDisabledStatus);
  };

  const applyTeamMemberStatusChange = async (teamMemberId, newDisabledStatus) => {
    try {
      setActionLoading(true);
      await TeamMemberService.updateTeamMemberStatus(teamMemberId, newDisabledStatus);
      await loadTeamMembers();

      if (newDisabledStatus) {
        setStatusSnackbar({
          open: true,
          message: t('teamMembers.billing.disabledNotice'),
        });
      }
    } catch (err) {
      console.error('Error toggling team member status:', err);
      const errorCode = err?.response?.data?.code;
      if (errorCode === 'TeamMemberBillingSyncFailed') {
        setStatusSnackbar({
          open: true,
          message: t('teamMembers.billing.syncFailed'),
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnableBillingConfirm = async () => {
    const teamMemberId = pendingEnableTeamMemberId;
    setShowEnableBillingConfirm(false);
    setPendingEnableTeamMemberId(null);
    if (teamMemberId) {
      await applyTeamMemberStatusChange(teamMemberId, false);
    }
  };

  const handlePictureFileChange = async (event) => {
    const file = event.target.files?.[0];
    const teamMemberId = pendingPictureTeamMemberIdRef.current;
    setPictureModalOpen(false);
    setPictureModalTeamMember(null);
    setPictureError(null);
    if (!file || !teamMemberId || !storeId) {
      event.target.value = '';
      return;
    }
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setPictureError(t(validation.errorKey, validation.errorParams));
      event.target.value = '';
      return;
    }
    pendingPictureTeamMemberIdRef.current = null;
    try {
      setPictureUploadingId(teamMemberId);
      const resized = await resizeImageForUpload(file);
      const response = await TeamMemberService.setProfilePicture(teamMemberId, resized);
      setTeamMembers(prev => prev.map(tm => tm.id === teamMemberId ? { ...tm, picture: response?.picture ?? response?.pictureUrl ?? response?.PictureUrl } : tm));
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setPictureError(err.response?.data?.message ?? t('teamMembers.picture.uploadError'));
    } finally {
      setPictureUploadingId(null);
      event.target.value = '';
    }
  };

  const handlePictureEditBadgeClick = (teamMember) => {
    setPictureError(null);
    setPictureModalTeamMember(teamMember);
    setPictureModalOpen(true);
  };

  const handlePictureModalChangePicture = () => {
    if (pictureModalTeamMember) {
      pendingPictureTeamMemberIdRef.current = pictureModalTeamMember.id;
      pictureFileInputRef.current?.click();
    }
  };

  const handlePictureModalRemove = () => {
    if (pictureModalTeamMember) {
      setPictureDeleteTarget(pictureModalTeamMember);
      setPictureModalOpen(false);
      setPictureModalTeamMember(null);
      setConfirmDeletePictureOpen(true);
    }
  };

  const handleDeletePictureConfirm = async () => {
    if (!pictureDeleteTarget || !storeId) {
      setConfirmDeletePictureOpen(false);
      setPictureDeleteTarget(null);
      return;
    }
    const { id } = pictureDeleteTarget;
    try {
      setPictureDeletingId(id);
      await TeamMemberService.deleteProfilePicture(id);
      setTeamMembers(prev => prev.map(tm => tm.id === id ? { ...tm, picture: null, Picture: null } : tm));
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setPictureError(err.response?.data?.message ?? t('teamMembers.picture.deleteError'));
    } finally {
      setPictureDeletingId(null);
      setConfirmDeletePictureOpen(false);
      setPictureDeleteTarget(null);
    }
  };

  // Render services with expand/collapse functionality - reactive to language changes
  const renderServices = useCallback((services, teamMemberId) => {

    if (!services || services.length === 0) {
      return <Typography variant="body2" color="text.secondary">{t('common.notProvided')}</Typography>;
    }

    const maxVisible = 2;
    const isExpanded = expandedServices.has(teamMemberId);
    const visibleServices = isExpanded ? services : services.slice(0, maxVisible);
    const remainingCount = services.length - maxVisible;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
        {visibleServices.map((service, index) => {
          const serviceName = getServiceName(service.name, i18n.language);
          const displayLabel = serviceName.length > 30 ? `${serviceName.slice(0, 27)}...` : serviceName;
          return (
            <Chip
              key={service.id || index}
              label={displayLabel}
              size="small"
              title={serviceName}
              sx={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontSize: '0.75rem',
              }}
            />
          );
        })}
        {remainingCount > 0 && !isExpanded && (
          <Chip
            label={`+${remainingCount} ${t('teamMembers.services.moreServices').split(' ').slice(1).join(' ')}`}
            size="small"
            sx={{
              backgroundColor: '#f5f5f5',
              color: '#666',
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: BRAND.line
              }
            }}
            onClick={() => toggleServicesExpansion(teamMemberId)}
          />
        )}
        {isExpanded && remainingCount > 0 && (
          <Chip
            label={t('teamMembers.services.showLess')}
            size="small"
            sx={{
              backgroundColor: '#f5f5f5',
              color: '#666',
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: BRAND.line
              }
            }}
            onClick={() => toggleServicesExpansion(teamMemberId)}
          />
        )}
      </Box>
    );
  }, [i18n.language, t, expandedServices, toggleServicesExpansion]);

  if (loading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('teamMembers.title')}
        pageSubtitle={t('teamMembers.subtitle')}
        variant="cards"
        withAction={hasPermission(Permissions.TeamMembersCreate)}
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadTeamMembers}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <ManagementPageLayout
      id="team-members-page"
      containerId="team-members-header"
      titleId="team-members-title"
      subtitleId="team-members-subtitle"
      pageTitle={t('teamMembers.title')}
      pageSubtitle={t('teamMembers.subtitle')}
      contentId="team-members-main-content"
    >
      <input
        ref={pictureFileInputRef}
        type="file"
        accept={ALLOWED_IMAGE_EXTENSIONS.join(',')}
        onChange={handlePictureFileChange}
        style={{ display: 'none' }}
      />
      <ManagementPageCard id="team-members-card">
          {/* Content */}
          <Box id="team-members-content" sx={{ p: 4 }}>
            {pictureError && (
              <Alert severity="error" onClose={() => setPictureError(null)} sx={{ mb: 2 }}>
                {pictureError}
              </Alert>
            )}
            {/* Add New Team Member */}
            {hasPermission(Permissions.TeamMembersCreate) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  mb: 4,
                  pt: 1,
                }}
              >
                {allowsExtraMembers ? (
                  <PrimaryActionButton
                    id="team-members-add-new-button"
                    component={Link}
                    to={`/management/stores/${storeId}/team-members/register`}
                    startIcon={<AddIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {t('teamMembers.addNew')}
                  </PrimaryActionButton>
                ) : (
                  <PrimaryActionButton
                    id="team-members-add-new-button"
                    onClick={openProUpgrade}
                    startIcon={<AddIcon />}
                    endIcon={<ProCrownIcon size={12} />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {t('teamMembers.addNew')}
                  </PrimaryActionButton>
                )}
              </Box>
            )}

            {/* Team Members List */}
            {filteredTeamMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  {t('teamMembers.noTeamMembers')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
                {filteredTeamMembers.map((teamMember) => (
                  <Grid item xs={12} sm={6} md={4} key={teamMember.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                        {/* Picture + name row */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'flex-start' },
                            textAlign: { xs: 'center', sm: 'left' },
                            gap: 2,
                            mb: 2,
                            overflow: 'visible',
                          }}
                        >
                          <Box
                            className="picture-block"
                            onClick={hasPermission(Permissions.TeamMembersUpdate) && !pictureUploadingId && !pictureDeletingId ? () => handlePictureEditBadgeClick(teamMember) : undefined}
                            sx={{
                              flexShrink: 0,
                              position: 'relative',
                              display: 'inline-flex',
                              overflow: 'visible',
                              cursor: hasPermission(Permissions.TeamMembersUpdate) ? 'pointer' : 'default',
                            }}
                          >
                            <Avatar
                              src={teamMember.picture ?? teamMember.Picture}
                              sx={{
                                width: 56,
                                height: 56,
                                bgcolor: brandAlpha('accent', 0.15),
                                border: `1px solid ${brandAlpha('accent', 0.3)}`,
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 30, color: BRAND.accentText }} />
                            </Avatar>
                            {hasPermission(Permissions.TeamMembersUpdate) && (
                              <IconButton
                                size="small"
                                disabled={!!pictureUploadingId || !!pictureDeletingId}
                                sx={{
                                  position: 'absolute',
                                  bottom: -2,
                                  right: -2,
                                  width: 22,
                                  height: 22,
                                  minWidth: 22,
                                  minHeight: 22,
                                  padding: 0,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  border: `1px solid ${brandAlpha('accent', 0.4)}`,
                                  boxShadow: 1,
                                  color: BRAND.accentText,
                                  pointerEvents: 'none',
                                  '& .MuiSvgIcon-root': { fontSize: 14 },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1, width: '100%' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-word' }}>
                              {teamMember.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 1,
                                overflow: 'hidden',
                                textOverflow: { xs: 'clip', sm: 'ellipsis' },
                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                wordBreak: { xs: 'break-word', sm: 'normal' },
                              }}
                              title={teamMember.email}
                            >
                              {teamMember.email}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                            {teamMember.jobTitle && (
                              <Chip
                                label={teamMember.jobTitle}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                            <Chip
                              label={teamMember.disabled ? t('teamMembers.status.inactive') : t('teamMembers.status.active')}
                              size="small"
                              sx={{
                                backgroundColor: teamMember.disabled ? '#ffebee' : '#e8f5e8',
                                color: teamMember.disabled ? '#c62828' : '#2e7d32',
                                fontSize: '0.75rem'
                              }}
                            />
                            {!teamMember.disabled && (
                              <Chip
                                label={memberAcceptsCustomerBookings(teamMember)
                                  ? t('teamMembers.status.acceptsBookings')
                                  : t('teamMembers.status.noCustomerBookings')}
                                size="small"
                                sx={{
                                  backgroundColor: memberAcceptsCustomerBookings(teamMember) ? '#e3f2fd' : '#fff3e0',
                                  color: memberAcceptsCustomerBookings(teamMember) ? '#1565c0' : '#ef6c00',
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                            </Box>
                          </Box>
                        </Box>

                        {/* Services */}
                        <Box sx={{ mb: 2, flexGrow: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('teamMembers.table.services')}:
                          </Typography>
                          {renderServices(teamMember.services, teamMember.id)}
                        </Box>

                        {/* Actions */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: { xs: 'space-evenly', sm: 'flex-start' },
                            width: '100%',
                            gap: 0.5,
                            pt: 2,
                            borderTop: `1px solid ${BRAND.line}`,
                            mt: 'auto',
                          }}
                        >
                            {hasPermission(Permissions.TeamMembersRead) && (
                              <Tooltip title={t('teamMembers.actions.view')}>
                                <IconButton
                                  size="small"
                                  component={Link}
                                  to={`/management/stores/${storeId}/team-members/${teamMember.id}`}
                                  sx={{ ...cardActionButtonSx, color: BRAND.accentText }}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canManageCompensation && (
                              <Tooltip title={t('teamMembers.actions.manageCompensation')}>
                                <IconButton
                                  size="small"
                                  component={isPro ? Link : 'button'}
                                  to={isPro ? `/management/stores/${storeId}/team-members/${teamMember.id}/compensation` : undefined}
                                  onClick={isPro ? undefined : openProUpgrade}
                                  sx={{ ...cardActionButtonSx, color: BRAND.accentText, position: 'relative' }}
                                >
                                  <PaymentsIcon />
                                  {!isPro ? (
                                    <Box sx={{ position: 'absolute', top: -4, right: -4 }}>
                                      <ProCrownIcon size={10} />
                                    </Box>
                                  ) : null}
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission(Permissions.TeamMembersUpdate) && (
                              <Tooltip title={t('teamMembers.actions.manageAgenda')}>
                                <IconButton
                                  size="small"
                                  component={Link}
                                  to={`/management/stores/${storeId}/team-members/${teamMember.id}/agenda`}
                                  aria-label={t('teamMembers.actions.manageAgenda')}
                                  sx={{ ...cardActionButtonSx, color: BRAND.accentText }}
                                >
                                  <AgendaIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission(Permissions.TeamMembersUpdate) && (
                              <Tooltip title={teamMember.disabled ? t('teamMembers.actions.enable') : t('teamMembers.actions.disable')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleStatus(teamMember.id)}
                                  disabled={actionLoading}
                                  sx={{
                                    ...cardActionButtonSx,
                                    color: teamMember.disabled ? '#f44336' : '#4caf50',
                                    '&:hover': {
                                      backgroundColor: teamMember.disabled ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'
                                    }
                                  }}
                                >
                                  {actionLoading ? <CircularProgress size={20} /> : (teamMember.disabled ? <EnableIcon /> : <DisableIcon />)}
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </ManagementPageCard>

      <ScrollableDialog
        open={pictureModalOpen}
        onClose={() => { setPictureModalOpen(false); setPictureModalTeamMember(null); }}
        maxWidth="xs"
        aria-labelledby="profile-picture-dialog-title"
        paperSx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
        title={<Typography id="profile-picture-dialog-title" component="h2" variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('teamMembers.picture.modalTitle')}</Typography>}
        subtitle={t('teamMembers.picture.modalDescription')}
        contentSx={{ pt: 1 }}
      >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={pictureUploadingId === pictureModalTeamMember?.id ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              onClick={handlePictureModalChangePicture}
              disabled={!!pictureUploadingId || !!pictureDeletingId}
              sx={{
                minHeight: 44,
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: BRAND.accentText,
                borderColor: brandAlpha('accent', 0.5),
                borderRadius: 1.5,
                py: 1.25,
                '&:hover': {
                  borderColor: BRAND.accent,
                  bgcolor: brandAlpha('accent', 0.06),
                },
              }}
            >
              {pictureUploadingId === pictureModalTeamMember?.id ? t('teamMembers.picture.uploading') : t('teamMembers.picture.upload')}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={pictureDeletingId === pictureModalTeamMember?.id ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
              onClick={handlePictureModalRemove}
              disabled={!!pictureUploadingId || !!pictureDeletingId}
              sx={{
                minHeight: 44,
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                borderRadius: 1.5,
                py: 1.25,
                borderColor: alpha('#f44336', 0.5),
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: alpha('#f44336', 0.06),
                },
              }}
            >
              {pictureDeletingId === pictureModalTeamMember?.id ? t('teamMembers.picture.deleting') : t('teamMembers.picture.delete')}
            </Button>
            <Button
              fullWidth
              onClick={() => { setPictureModalOpen(false); setPictureModalTeamMember(null); }}
              sx={{
                minHeight: 40,
                textTransform: 'none',
                fontSize: '0.9375rem',
                color: 'text.secondary',
                mt: 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {t('common.cancel')}
            </Button>
          </Box>
      </ScrollableDialog>

      <ConfirmDialog
        open={confirmDeletePictureOpen}
        onClose={() => { setConfirmDeletePictureOpen(false); setPictureDeleteTarget(null); }}
        onConfirm={handleDeletePictureConfirm}
        title={t('common.confirm')}
        message={t('teamMembers.picture.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
      />

      <TeamMemberBillingConfirmDialog
        open={showEnableBillingConfirm}
        onClose={() => {
          setShowEnableBillingConfirm(false);
          setPendingEnableTeamMemberId(null);
        }}
        onConfirm={handleEnableBillingConfirm}
        billingImpact={enableBillingImpact}
        loading={actionLoading}
        confirmLabelKey="teamMembers.billing.confirmEnable"
      />

      <Snackbar
        open={statusSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setStatusSnackbar({ open: false, message: '' })}
        message={statusSnackbar.message}
      />
    </ManagementPageLayout>
  );
};

export default TeamMembersPage;